import { useState, useRef, useEffect } from "react";
import { FiChevronDown, FiX, FiCheck } from "react-icons/fi";

interface Option {
  value: string;
  label: string;
}

interface MultiSelectDropdownProps {
  options: Option[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  error?: string;
  name?: string;
}

export function MultiSelectDropdown({
  options,
  value,
  onChange,
  placeholder = "Selecione...",
  error,
}: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fechar ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter(v => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const clearAll = () => {
    onChange([]);
  };

  const getSelectedLabels = () => {
    return options
      .filter(opt => value.includes(opt.value))
      .map(opt => opt.label);
  };

  const getBorderClass = () => {
    if (error) {
      return "border-red-500";
    }
    if (isOpen) {
      return "border-[#0085BD] ring-1 ring-[#0085BD]";
    }
    return "border-gray-200 hover:border-[#0085BD]";
  };

  const renderSelectedDisplay = () => {
    if (value.length === 0) {
      return <span className="text-gray-400 text-sm">{placeholder}</span>;
    }

    if (value.length <= 2) {
      return (
        <div className="flex items-center gap-1 flex-wrap">
          {getSelectedLabels().map(label => (
            <span
              key={label}
              className="px-2 py-0.5 bg-[#0085BD]/10 text-[#0085BD] text-xs rounded-full truncate max-w-[120px]"
            >
              {label}
            </span>
          ))}
        </div>
      );
    }

    return (
      <span className="text-sm text-gray-700">
        {value.length} itens selecionados
      </span>
    );
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full h-[52px] px-4 bg-white border rounded-lg flex items-center justify-between transition-colors ${getBorderClass()}`}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden">
          {renderSelectedDisplay()}
        </div>
        <div className="flex items-center gap-2">
          {value.length > 0 && (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                clearAll();
              }}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              title="Limpar seleção"
            >
              <FiX className="text-gray-400" size={14} />
            </button>
          )}
          <FiChevronDown
            className={`text-gray-400 transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
            size={18}
          />
        </div>
      </button>

      {/* Dropdown List */}
      {isOpen && (
        <div
          className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
        >
          <div className="max-h-64 overflow-y-auto">
            {value.length > 0 && (
              <button
                type="button"
                onClick={clearAll}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 border-b border-gray-100 flex items-center gap-2 sticky top-0 bg-white"
              >
                <FiX size={14} />
                Limpar seleção ({value.length})
              </button>
            )}
            {options.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => toggleOption(option.value)}
                className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                  value.includes(option.value) ? "bg-blue-50" : ""
                }`}
              >
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors shrink-0 ${
                    value.includes(option.value)
                      ? "border-[#0085BD] bg-[#0085BD]"
                      : "border-gray-300"
                  }`}
                >
                  {value.includes(option.value) && (
                    <FiCheck className="text-white" size={12} />
                  )}
                </div>
                <span className="text-gray-700 truncate">{option.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {error && <div className="text-xs text-red-500 mt-1">{error}</div>}
    </div>
  );
}
